import {
  Component,
  computed,
  effect,
  ElementRef,
  OnDestroy,
  OnInit,
  signal,
  viewChild,
} from '@angular/core';
import {DatePipe} from '@angular/common';
import {Button} from 'primeng/button';
import {
  AdminService,
  AtoTier,
  FullUserDetails,
  JobRunStatus,
  JobStatusDetails,
  UserGroupDetails, UserType,
} from '../../../api';
import {LoadingService} from '../../../services/loading.service';
import {ErrorMessageService} from '../../../services/error-message.service';
import {TableModule} from 'primeng/table';
import {TaAppStore} from '../../../state/clcta-store';
import {Card} from 'primeng/card';
import {FormsModule} from '@angular/forms';
import {InputText} from 'primeng/inputtext';
import {PermissionService} from '../../../services/permission.service';
import {DialogService, DynamicDialogRef} from 'primeng/dynamicdialog';
import {ManageUsersModalComponent} from '../../components/admin/manage-users-modal/manage-users-modal.component';
import {Tag} from 'primeng/tag';
import {ScrollHeightDirective} from '../../directives/scroll-height.directive';
import {getUserTypeSuffix} from '../../../util/general.utils';

@Component({
  selector: 'user-admin',
  imports: [
    Button,
    TableModule,
    Card,
    DatePipe,
    FormsModule,
    InputText,
    Tag,
    ScrollHeightDirective,
  ],
  templateUrl: './clc-user-admin.html',
  styleUrl: './clc-user-admin.scss',
  providers: [DialogService],
})
export class ClcUserAdmin implements OnInit, OnDestroy {
  private scrollContainer = viewChild<ElementRef>('scrollContainer');
  private loadMoreSentinel = viewChild<ElementRef>('loadMoreSentinel');
  private observer: IntersectionObserver | undefined;

  userList = signal<FullUserDetails[]>([]);
  tierMap = signal<Map<string, AtoTier>>(new Map<string, AtoTier>());
  tierList = signal<AtoTier[]>([]);

  searchStr = signal('');
  totalCount = signal(0);
  currentPage = signal(0);
  hasSearched = signal(false);
  isSearching = signal(false);

  canLoadMore = computed(() => this.userList().length < this.totalCount());

  // Entra refresh job status panel
  jobStatus = signal<JobStatusDetails | null>(null);
  jobStatusLoaded = signal(false);
  private awaitingRunStart = signal(false);
  isRefreshRunning = computed(() =>
    this.jobStatus()?.status === JobRunStatus.Running || this.awaitingRunStart());
  canManageUsers: boolean = false;
  private pollTimer: ReturnType<typeof setInterval> | null = null;
  private pendingTriggerStarted: string | null = null;
  private pendingTriggerAt: number | null = null;
  private readonly JOB_POLL_INTERVAL = 5 * 1000;
  // How long to keep polling for a triggered run to appear before giving up
  private readonly TRIGGER_START_TIMEOUT = 30 * 1000;

  isSystemAdmin: boolean = false;

  ref: DynamicDialogRef | null = null;

  constructor(
    private store: TaAppStore,
    private adminApi: AdminService,
    private loadingService: LoadingService,
    private permissionService: PermissionService,
    private dialogService: DialogService,
    private errorMessageService: ErrorMessageService,
  ) {
    this.isSystemAdmin = this.permissionService.isSystemAdmin();
    this.canManageUsers = this.permissionService.canManageUsers();

    effect(() => {
      const el = this.loadMoreSentinel()?.nativeElement;
      const root = this.scrollContainer()?.nativeElement;
      this.observer?.disconnect();
      if (el && root) {
        this.observer = new IntersectionObserver(
          (entries) => {
            if (entries[0].isIntersecting) {
              this.loadMore();
            }
          },
          { root, threshold: 0.1 },
        );
        this.observer.observe(el);
      }
    });
  }

  ngOnInit(): void {
    this.loadAtoRates();
    if (this.canManageUsers) {
      this.loadJobStatus();
    }
    this.store.updateBreadCrumbData({
      title: 'Users',
      titleIcon: 'fa-regular fa-users fa-lg'
    });
  }

  ngOnDestroy(): void {
    this.observer?.disconnect();
    this.stopJobPolling();
  }

  loadJobStatus(): void {
    this.adminApi.userJobStatus().subscribe({
      next: resp => {
        if (resp.success && resp.data) {
          this.jobStatus.set(resp.data);
        }
        this.jobStatusLoaded.set(true);
        this.updateJobPolling();
      },
      error: error => {
        console.error('Error loading Entra refresh job status: ', error);
        // Enable the button anyway — the backend refuses a trigger if the job is genuinely running
        this.jobStatusLoaded.set(true);
      }
    });
  }

  triggerEntraRefresh(): void {
    if (this.isRefreshRunning()) return;

    this.adminApi.refreshUsersFromEntra().subscribe({
      next: resp => {
        if (resp.success) {
          // The trigger returns the pre-run status; poll until a new run appears and finishes
          this.pendingTriggerStarted = resp.data?.lastStarted ?? null;
          this.pendingTriggerAt = Date.now();
          this.awaitingRunStart.set(true);
          this.startJobPolling();
        } else {
          this.errorMessageService.showTemporaryError(resp.error ?? 'Failed to trigger Entra user refresh');
          this.loadJobStatus();
        }
      },
      error: error => {
        console.error('Error triggering Entra user refresh: ', error);
        this.errorMessageService.showTemporaryError('Failed to trigger Entra user refresh');
      }
    });
  }

  private updateJobPolling(): void {
    if (this.jobStatus()?.status === JobRunStatus.Running) {
      this.clearPendingTrigger();
      this.startJobPolling();
      return;
    }

    if (this.awaitingRunStart()) {
      // Not running yet — keep polling until the triggered run shows up (new lastStarted) or we time out
      const runSeen = this.jobStatus()?.lastStarted !== this.pendingTriggerStarted;
      const timedOut = this.pendingTriggerAt !== null && Date.now() - this.pendingTriggerAt > this.TRIGGER_START_TIMEOUT;
      if (!runSeen && !timedOut) {
        this.startJobPolling();
        return;
      }
      this.clearPendingTrigger();
    }

    this.stopJobPolling();
  }

  private clearPendingTrigger(): void {
    this.awaitingRunStart.set(false);
    this.pendingTriggerStarted = null;
    this.pendingTriggerAt = null;
  }

  private startJobPolling(): void {
    if (this.pollTimer === null) {
      this.pollTimer = setInterval(() => this.loadJobStatus(), this.JOB_POLL_INTERVAL);
    }
  }

  private stopJobPolling(): void {
    if (this.pollTimer !== null) {
      clearInterval(this.pollTimer);
      this.pollTimer = null;
    }
  }

  loadAtoRates(): void {
    this.adminApi.getAtoRates().subscribe({
      next: resp => {
        this.tierList.set(resp.data?.filter(tier => tier.name !== 'All') ?? []);
        let map = new Map<string, AtoTier>();
        for (const tier of resp.data!) {
          map.set(tier!.id!, tier);
        }
        this.tierMap.set(map);
      },
      error: error => {
        console.log('Error loading rates: ', error);
      }
    });
  }

  getGroupStr(groups: UserGroupDetails[]): string {
    if (groups.length === 0) {
      return '-';
    }
    return groups.map(g => g.name).join(', ');
  }

  runSearch(): void {
    this.currentPage.set(0);
    this.userList.set([]);
    this.isSearching.set(true);
    this.loadingService.showLoading({message: 'Searching Users'});

    this.adminApi.searchTaUsers(0, this.searchStr()).subscribe({
      next: resp => {
        if (resp.success && resp.data) {
          this.userList.set(resp.data.results ?? []);
          this.totalCount.set(resp.data.totalCount ?? 0);
        }
        this.hasSearched.set(true);
        this.isSearching.set(false);
        this.loadingService.hideLoading();
      },
      error: error => {
        console.error('Error searching users: ', error);
        this.isSearching.set(false);
        this.loadingService.hideLoading();
      }
    });
  }

  loadMore(): void {
    if (!this.canLoadMore() || this.isSearching()) return;
    this.isSearching.set(true);
    const nextPage = this.currentPage() + 1;
    this.currentPage.set(nextPage);

    this.adminApi.searchTaUsers(nextPage, this.searchStr()).subscribe({
      next: resp => {
        if (resp.success && resp.data) {
          this.userList.update(list => [...list, ...(resp.data!.results ?? [])]);
          this.totalCount.set(resp.data.totalCount ?? this.totalCount());
        }
        this.isSearching.set(false);
      },
      error: error => {
        console.error('Error loading more users: ', error);
        this.isSearching.set(false);
      }
    });
  }

  clear(): void {
    this.searchStr.set('');
    this.userList.set([]);
    this.totalCount.set(0);
    this.currentPage.set(0);
    this.hasSearched.set(false);
  }

  openEditUserModal(user: FullUserDetails): void {
    this.ref = this.dialogService.open(ManageUsersModalComponent, {
      header: 'Edit User',
      inputValues: {
        modalData: user,
        tierList: this.tierList(),
      },
      contentStyle: {
        overflow: 'auto',
      },
      modal: true,
      width: 'fit',
    });
    this.ref?.onClose.subscribe((data: FullUserDetails | undefined) => {
      if (!!data) {
        let updatedUser: FullUserDetails = {...user, ...data};
        this.adminApi.updateUserDetails(updatedUser).subscribe({
          next: (response) => {
            if (response.success && !!response.data) {
              const currentList = this.userList();
              const index = currentList.findIndex((u) => u.id === response.data!.id);
              if (index !== -1) {
                const newList = [...currentList];
                newList[index] = response.data!;
                this.userList.set(newList);
              }
            } else {
              console.error('Error updating user', response.error);
            }
          },
          error: (error) => {
            console.error('Error updating user', error);
          },
        });
      }
    });
  }

  protected readonly getUserTypeSuffix = getUserTypeSuffix;
  protected readonly UserType = UserType;
  protected readonly JobRunStatus = JobRunStatus;
}
